<template>
  <div class="catalogo-virtual-table elevation-1">
    <div v-if="loading" class="catalogo-virtual-loading">
      Buscando dados...
    </div>

    <template v-else>
      <div class="catalogo-virtual-header catalogo-virtual-grid" :style="gridStyle">
        <div
          v-for="header in headers"
          :key="header.value"
          class="catalogo-virtual-cell catalogo-virtual-th"
          :class="cellClass(header)"
        >
          {{ header.text }}
        </div>
      </div>

      <div
        ref="scroller"
        class="catalogo-virtual-body"
        :style="{ height: height + 'px' }"
        @scroll="onScroll"
      >
        <div :style="{ height: totalHeight + 'px', position: 'relative', width: '100%' }">
          <div
            class="catalogo-virtual-rows"
            :style="{ transform: 'translateY(' + offsetY + 'px)' }"
          >
            <div
              v-for="entry in visibleItems"
              :key="rowKey(entry.item, entry.index)"
              class="catalogo-virtual-row catalogo-virtual-grid"
              :style="rowStyle"
            >
              <div
                v-for="header in headers"
                :key="header.value"
                class="catalogo-virtual-cell"
                :class="cellClass(header)"
              >
                <slot :name="'item.' + header.value" :item="entry.item">
                  <span class="catalogo-virtual-text" :title="itemValue(entry.item, header.value)">
                    {{ itemValue(entry.item, header.value) }}
                  </span>
                </slot>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="catalogo-virtual-footer">
        <span>{{ items.length }} itens</span>
        <v-select
          class="catalogo-virtual-page-size"
          dense
          hide-details
          :items="pageOptions"
          item-text="text"
          item-value="value"
          :value="itemsPerPage"
          label="Items por pagina"
          @change="$emit('update:items-per-page', $event)"
        />
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "CatalogoVirtualTable",
  props: {
    headers: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: -1,
    },
    itemKey: {
      type: String,
      default: "id",
    },
    height: {
      type: Number,
      default: 520,
    },
    rowHeight: {
      type: Number,
      default: 72,
    },
    buffer: {
      type: Number,
      default: 5,
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [5, 10, 15, 50, 100, -1],
    },
  },
  data: () => ({
    scrollTop: 0,
    frame: null,
  }),
  computed: {
    totalHeight() {
      return this.items.length * this.rowHeight;
    },
    startIndex() {
      return Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - this.buffer);
    },
    visibleCount() {
      return Math.ceil(this.height / this.rowHeight) + this.buffer * 2;
    },
    endIndex() {
      return Math.min(this.items.length, this.startIndex + this.visibleCount);
    },
    offsetY() {
      return this.startIndex * this.rowHeight;
    },
    visibleItems() {
      return this.items.slice(this.startIndex, this.endIndex).map((item, index) => ({
        item,
        index: this.startIndex + index,
      }));
    },
    pageOptions() {
      return this.itemsPerPageOptions.map((option) => ({
        text: option === -1 ? "Todos" : String(option),
        value: option,
      }));
    },
    gridStyle() {
      return {
        gridTemplateColumns: this.headers.map((header) => this.columnTemplate(header)).join(" "),
      };
    },
    rowStyle() {
      return {
        height: this.rowHeight + "px",
        gridTemplateColumns: this.gridStyle.gridTemplateColumns,
      };
    },
  },
  watch: {
    items() {
      this.scrollTop = 0;
      this.$nextTick(() => {
        if (this.$refs.scroller) {
          this.$refs.scroller.scrollTop = 0;
        }
      });
    },
  },
  beforeDestroy() {
    if (this.frame) {
      cancelAnimationFrame(this.frame);
    }
  },
  methods: {
    onScroll(event) {
      const nextScrollTop = event.target.scrollTop;
      if (this.frame) {
        cancelAnimationFrame(this.frame);
      }
      this.frame = requestAnimationFrame(() => {
        this.scrollTop = nextScrollTop;
        this.frame = null;
      });
    },
    rowKey(item, index) {
      return item && item[this.itemKey] != null ? item[this.itemKey] : index;
    },
    itemValue(item, value) {
      return value.split(".").reduce((current, key) => {
        return current == null ? "" : current[key];
      }, item);
    },
    cellClass(header) {
      return "catalogo-virtual-cell--" + header.value;
    },
    columnTemplate(header) {
      const templates = {
        id: "42px",
        descricao: "minmax(220px, 3fr)",
        codigo: "minmax(130px, 1.35fr)",
        unidadeMedida: "minmax(60px, 0.6fr)",
        departamento: "minmax(90px, 0.9fr)",
        tipoProdutoServico: "minmax(90px, 0.9fr)",
        quantidade: "minmax(90px, 0.9fr)",
        observacao: "minmax(110px, 1.1fr)",
        actions: "56px",
      };

      return templates[header.value] || "minmax(64px, 1fr)";
    },
  },
};
</script>

<style scoped>
.catalogo-virtual-table {
  background: #fff;
  border-radius: 4px;
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}

.catalogo-virtual-loading {
  padding: 24px;
  text-align: center;
}

.catalogo-virtual-grid {
  box-sizing: border-box;
  display: grid;
  max-width: 100%;
  min-width: 0;
  width: 100%;
}

.catalogo-virtual-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.catalogo-virtual-body {
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
}

.catalogo-virtual-rows {
  left: 0;
  min-width: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
}

.catalogo-virtual-row {
  align-items: stretch;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.catalogo-virtual-cell {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  font-size: 0.875rem;
  line-height: 1.35;
  min-width: 0;
  overflow: hidden;
  padding: 0 8px;
  white-space: normal;
  word-break: normal;
}

.catalogo-virtual-cell > * {
  max-width: 100%;
  min-width: 0;
}

.catalogo-virtual-cell--actions {
  justify-content: center;
  padding-left: 4px;
  padding-right: 4px;
}

.catalogo-virtual-cell--id,
.catalogo-virtual-cell--unidadeMedida,
.catalogo-virtual-cell--quantidade {
  justify-content: center;
}

.catalogo-virtual-th {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.75rem;
  font-weight: 500;
  height: 48px;
  line-height: 1.2;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.catalogo-virtual-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-height: 1.35;
  min-width: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  white-space: normal;
  word-break: normal;
}

.catalogo-virtual-cell--codigo .catalogo-virtual-text {
  overflow-wrap: anywhere;
}

.catalogo-virtual-cell ::v-deep .v-input,
.catalogo-virtual-cell ::v-deep .v-input__control,
.catalogo-virtual-cell ::v-deep .v-input__slot {
  max-width: 100%;
  min-width: 0;
  width: 100%;
}

.catalogo-virtual-cell ::v-deep .v-text-field {
  margin-top: 0;
  padding-top: 0;
}

.catalogo-virtual-cell--actions ::v-deep .v-btn {
  flex-shrink: 0;
  margin-left: 0;
  margin-right: 0;
}

.catalogo-virtual-footer {
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  min-height: 58px;
  padding: 8px 16px;
}

.catalogo-virtual-page-size {
  max-width: 150px;
}

@media (max-width: 960px) {
  .catalogo-virtual-table {
    overflow-x: auto;
  }

  .catalogo-virtual-grid,
  .catalogo-virtual-body,
  .catalogo-virtual-rows {
    min-width: 920px;
  }
}
</style>